import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Container, Row, Col } from 'react-bootstrap';
import { Lightbox } from '../../../../src/web/components';
import Asset from '../../../components/stateless/Asset';
import { resolveContentUrl } from '../../../../modules/assets';
import { pushRoute } from '../../../../modules/routing';
import MediaGalleryImage from '../../../../modules/react/components/stateful/MediaGalleryImage';
import { TRACK_IMAGE_GALLERY_INTERACTION } from '../../../../src/web/utils/dataLayer';
import styles from './styles.scss';
import useEventTrigger from '../../../../modules/react/hooks/useEventTrigger';

const propTypes = {
  images: PropTypes.arrayOf(PropTypes.shape({
    medium: PropTypes.shape(),
    file: PropTypes.string,
    alt: PropTypes.string,
    link: PropTypes.string,
    target: PropTypes.string,
    title: PropTypes.string,
  })),
  isBuilderView: PropTypes.bool,
};

const defaultProps = {
  images: [],
  isBuilderView: false,
};

const ImageGrid = ({ images, isBuilderView }) => {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [lightBoxChangeCount, setLightBoxChangeCount] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const { addEvent } = useEventTrigger();

  const trackAnalytics = () => {
    const defaultPayload = {
      timeStamp: Date.now(),
    };

    const analyticsPayload = {
      timeSpent: elapsedTime,
      imagesViewed: lightBoxChangeCount,
      ...defaultPayload,
    };
    addEvent(TRACK_IMAGE_GALLERY_INTERACTION, analyticsPayload, 'click');
  };

  useEffect(() => {
    let timer;

    if (lightboxIndex !== null) {
      setLightboxOpen(true);

      timer = setInterval(() => {
        setElapsedTime(prevTime => prevTime + 1);
      }, 1000);

      setLightBoxChangeCount(prevCount => prevCount + 1);
    }

    if (lightboxIndex === null && lightboxOpen) {
      trackAnalytics();
      setLightboxOpen(false);
      setElapsedTime(0);
      setLightBoxChangeCount(0);
    }

    return () => clearInterval(timer);
  }, [lightboxIndex]);

  const renderAsset = (image, index) => {
    const { medium, file, alt, title, staticImg } = image;
    if (!file && !medium) {
      return false;
    }

    const sharedProps = {
      className: cx(
        'img-fluid',
        styles.image,
      ),
    };

    const handleOnClick = () => setLightboxIndex(index);
    return !staticImg ? (
      <button
        className={styles.lightboxButton}
        onClick={handleOnClick}
        type="button"
      >
        <MediaGalleryImage
          medium={medium}
          legacyImage={image}
          {...sharedProps}
        />
      </button>
    ) : (
      <Asset
        file={file}
        alt={alt}
        title={title}
        onClick={handleOnClick}
        {...sharedProps}
      />
    );
  };

  const renderImage = (
    image,
    index,
  ) => (
    <Col
      key={`image-grid-image-${index}`}
      xs={12}
      sm={6}
      className={`${styles.assets} clearfix`}
    >
      {renderAsset(image, index)}
    </Col>
  );

  const renderLightbox = (rawImages) => {
    if (lightboxIndex === null || lightboxIndex < 0 || lightboxIndex >= rawImages.length) {
      return false;
    }

    const onClickImage = () => {
      const { link, target } = rawImages[lightboxIndex];
      if (!link) {
        return null;
      }
      if (target === '_blank') {
        return window.open(link);
      }
      return pushRoute(link);
    };

    const items = rawImages.map(img => (
      /* eslint-disable jsx-a11y/click-events-have-key-events */
      /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
      !img.staticImg ? (
        <MediaGalleryImage
          medium={img.medium}
          legacyImage={img}
          className={styles.lightboxItem}
          {...img}
        />
      ) : (
        <img
          src={resolveContentUrl(img.file)}
          alt={img.alt}
          onClick={onClickImage}
          className={styles.lightboxItem}
          title={img.title}
        />
      )
    ));
    /* eslint-enable jsx-a11y/click-events-have-key-events */
    /* eslint-enable jsx-a11y/no-noninteractive-element-interactions */

    const nextIndex = (lightboxIndex + 1) % items.length;
    const prevIndex = (lightboxIndex + (items.length - 1)) % items.length;

    const lightboxProps = {
      items,
      itemIndex: lightboxIndex,
      onClickClose: () => setLightboxIndex(null),
      onClickDot: setLightboxIndex,
      onClickNext: () => setLightboxIndex(nextIndex),
      onClickPrev: () => setLightboxIndex(prevIndex),
    };

    return <Lightbox {...lightboxProps} />;
  };

  if ((!images || !images.length) && isBuilderView) {
    return (
      <div>
        No images set.
      </div>
    );
  }

  return (
    <Container fluid className={styles.container}>
      <Row className={styles.assets}>
        {images.map((image, idx) => renderImage(image, idx))}
      </Row>
      {renderLightbox(images)}
    </Container>
  );
};

ImageGrid.propTypes = propTypes;
ImageGrid.defaultProps = defaultProps;

export default ImageGrid;
